import React from 'react';
import { useField } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { getHelpTextValue, normalizeFormikError, WrappedBaseInput, WrappedBaseInputProps } from './BaseInput';

const SHORT_TEXT_LINE_BREAK_REGEX = /[\n\r]{1,}/gm;
const LONG_TEXT_LINE_BREAK_REGEX = /[\n\r]{3,}/gm;
const normalizeNewLines = ({ textAreaValue, maxLength }: { textAreaValue: string; maxLength?: number }) => {
  // remove any leading whitespace, spaces, newlines, tabs, etc from the input string
  const resultString = textAreaValue.replace(/^[\r\s]+/, '');

  if (typeof maxLength === 'number' && maxLength <= 100) {
    // when maxLength is less than 100 replace any newline or line breaks with a single space
    return resultString.replace(SHORT_TEXT_LINE_BREAK_REGEX, ' ');
  }

  // when maxLength is greater than 100 replace any time 3 linebreaks or more happen with only two newlines
  // this allows for the user to input spacing between paragraphs without the veritcal space being wasted
  return resultString.replace(LONG_TEXT_LINE_BREAK_REGEX, '\n\n');
};

export type ConnectedTextAreaInputProps = {
  /** name of the field in the formik state */
  name: string;
  /** is the field disabled */
  disabled?: boolean;
  /** label for the input */
  label?: string;
  /** help text to display below the input */
  helpText?: string;
  /** type for textarea is hardcoded for this component */
  type?: never;
  /** props passed directly through to the underlying input, you should not need this in most cases */
  inputProps?: Omit<WrappedBaseInputProps['inputProps'], 'invalid' | 'onChange' | 'onBlur' | 'value' | 'type' | 'id'>;
  /** props passed directly through to the <FieldWrapper /> component, you should not need this in most cases */
  wrapperProps?: Omit<
    WrappedBaseInputProps['wrapperProps'],
    'id' | 'label' | 'helpText' | 'disabled' | 'validation' | 'tooltip' | 'helpTextAbove'
  >;
  /** tooltip to display next to the label */
  tooltip?: React.ReactNode;
  /** help text to display above the input */
  helpTextAbove?: string;
  /** prevents the user from entering a value longer than maxLength
   * also sets helpText below the input if maxLength is reached to `${maxLength} Character limit reached` */
  maxLength?: number;
  /** aria label for the input */
  ariaLabel?: string;
  /** number of rows to show in for the height of the textarea, defaults to 3 */
  rows?: number;
  /** requires the field to be touched before it shows a validation error, defaults to false */
  validationRequiresTouched?: boolean;
};

/**
 * A textarea input tied into the a Formik Field's state
 * @name TextAreaInput
 * @description this should be used as the normal textarea input field for all Formik forms.
 * See BaseInput for more details of inputProps and wrapperProps, though you should not need them in most cases
 *
 * @example
 * <TextareaInput name="input" label="My Input" /> // most basic
 * <TextareaInput name="input" label="My Input" helpText="Please Fill This Out" /> // with help text
 * <TextareaInput name="input" label="My Input" disabled={isDisabled} /> // disabled
 */
export const TextAreaInput = ({
  name,
  disabled = false,
  label,
  helpText: helpTextProp,
  wrapperProps,
  inputProps,
  tooltip,
  helpTextAbove,
  maxLength,
  ariaLabel,
  rows = 3,
  validationRequiresTouched = false,
}: ConnectedTextAreaInputProps) => {
  const { t } = useTranslation();
  const [input, meta, helpers] = useField<string>(name);
  const { value: formikValue, onBlur } = input;
  const { error, touched } = meta;
  const hasMaxLength = typeof maxLength === 'number';
  const helpText = getHelpTextValue({ maxLength, helpTextProp, currentValue: formikValue, t });
  const hasError = !!error;
  const isTouched = !!touched;
  const isInvalid = !validationRequiresTouched ? hasError : hasError && isTouched;
  const fieldValidation = isInvalid ? { invalid: true } : {};
  const wrapperValidation = isInvalid ? { validation: { valid: !hasError, message: normalizeFormikError(error) } } : {};

  const onChange = (newVal: string) => {
    let valToSet = normalizeNewLines({ textAreaValue: newVal, maxLength });

    if (hasMaxLength && newVal?.length >= maxLength) {
      valToSet = newVal.slice(0, maxLength);
    }

    helpers.setValue(valToSet);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined || inputValue === null) {
    inputValue = '';
  }

  return (
    <WrappedBaseInput
      wrapperProps={{
        label,
        helpText,
        ...wrapperProps,
        ...wrapperValidation,
        id: name,
        disabled,
        tooltip,
        helpTextAbove,
      }}
      inputProps={{
        ...inputProps,
        ...fieldValidation,
        value: inputValue,
        name,
        onChange,
        onBlur,
        disabled,
        type: 'textarea',
        className: 'cn_mt-1',
        'aria-label': ariaLabel,
        rows,
      }}
    />
  );
};
